import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SectionBackground from '../components/SectionBackground';

const CHECKOUT_URL = 'https://buy.stripe.com/5kQ00jaWY9767QG23n6EU03?locale=en';
const PORTFOLIO_MAILTO =
  'mailto:contact@tmrush.com?subject=Brazil%20Watch%20%E2%80%94%20portfolio%20rates';

// Os e-mails em /email/samples/ saem dos mesmos modelos que o sistema usa com os clientes,
// preenchidos com uma marca fictícia e com os totais reais das revistas do INPI de outubro/2026.
const samples = [
  {
    key: 'monthly',
    label: 'Monthly report',
    when: 'Last Friday of every month',
    title: 'The whole month in one email',
    text: 'Everything published in Brazil that month, checked against your mark week by week, and where each of your own applications stands.',
    points: [
      'How many new applications we checked, gazette by gazette',
      'Each of your applications with its stage and what comes next',
      'Sent even in quiet months, so silence never leaves you guessing',
    ],
    subject: 'NORDA · Brazil Trademark Watch · October 2026',
    src: '/email/samples/monthly-report.html',
  },
  {
    key: 'stage',
    label: 'Stage completed',
    when: 'The week your application moves forward',
    title: 'Every step forward, the same week',
    text: 'When INPI publishes progress on your application, you hear from us that week, with the stages you have completed and the next one.',
    points: [
      'Published for opposition',
      'Opposition period cleared',
      'Approved and registered',
    ],
    subject: 'Congratulations: NORDA cleared the opposition period in Brazil (class 3)',
    src: '/email/samples/stage-completed.html',
  },
  {
    key: 'conflict',
    label: 'Possible conflict',
    when: 'As soon as our specialist confirms a match',
    title: 'A clear alert, with the deadline counted',
    text: 'If someone files a mark like yours, a Brazilian specialist reviews it first. You get the comparison, the reasons and the exact date to oppose.',
    points: [
      'Only matches a specialist has reviewed',
      'Opposition deadline already counted for you',
      'Nothing is filed without your approval',
    ],
    subject: 'Possible conflict: NORDAH filed in Brazil in class 3 · oppose by 4 January 2027',
    src: '/email/samples/possible-conflict.html',
  },
  {
    key: 'ip-firms',
    label: 'For IP firms',
    when: 'The same reports, addressed to your team',
    title: 'Ready to forward to your client',
    text: 'When you instruct us as your Brazilian associate, the reports come to your team with your reference in the subject line, ready to forward or to use in your own reporting.',
    points: [
      'Your reference in every subject line',
      'Addressed to your team, ready to forward',
      'Portfolio rates from five watched marks',
    ],
    subject: 'Your ref: NOR-BR-01 · NORDA · Brazil Trademark Watch · October 2026',
    src: '/email/samples/monthly-report-ip-firm.html',
  },
];

const EmailFrame = ({ sample }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(900);

  // Como os apps de e-mail: se o e-mail for mais largo que o espaço, reduz para caber na largura.
  const fit = useCallback(() => {
    try {
      const frame = ref.current;
      const doc = frame && frame.contentDocument;
      if (!doc || !doc.documentElement) return;
      const root = doc.documentElement;
      root.style.zoom = '1';
      const natural = doc.body.getBoundingClientRect().height;
      const ratio = Math.min(1, frame.clientWidth / root.scrollWidth);
      root.style.zoom = String(ratio);
      setHeight(Math.ceil(natural * ratio));
    } catch (e) {
      /* mantém a altura atual */
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [fit]);

  return (
    <div className="-mx-4 sm:mx-0 sm:rounded-3xl bg-background-white border-y sm:border border-gray-100 shadow-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-background-auxiliary/60">
        <div className="flex items-center gap-2 mb-2" aria-hidden="true">
          <span className="w-3 h-3 rounded-full bg-gray-300" />
          <span className="w-3 h-3 rounded-full bg-gray-300" />
          <span className="w-3 h-3 rounded-full bg-gray-300" />
        </div>
        <p className="text-xs text-text/60">
          <span className="font-semibold text-text/80">From:</span> TM RUSH Brazil Trademark Watch
        </p>
        <p className="text-sm text-text font-semibold leading-snug mt-1">{sample.subject}</p>
      </div>
      <iframe
        key={sample.key}
        ref={ref}
        src={sample.src}
        title={`Sample email: ${sample.label}`}
        onLoad={fit}
        scrolling="no"
        className="w-full block border-0 bg-[#eef1ee]"
        style={{ height }}
      />
    </div>
  );
};

const BrazilWatchSamplePage = () => {
  const { hash } = useLocation();
  const initial = samples.find((s) => `#${s.key}` === hash) || samples[0];
  const [active, setActive] = useState(initial.key);
  const sample = samples.find((s) => s.key === active) || samples[0];

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Sample reports · Brazil Trademark Watch | TM RUSH';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const choose = (key) => {
    setActive(key);
    // troca o endereço sem rolar a página, para o link poder abrir direto num exemplo
    try {
      window.history.replaceState(null, '', `#${key}`);
    } catch (e) {
      /* sem histórico: só troca a aba */
    }
  };

  return (
    <main className="bg-background min-h-screen">
      <SectionBackground image="/images/bg-hero.svg" overlay="dark" className="pt-36 md:pt-44 pb-16 md:pb-20">
        <div className="container mx-auto px-4 text-white">
          <p className="text-primary-light font-semibold text-sm uppercase tracking-wider mb-4">
            Brazil Trademark Watch · Samples
          </p>
          <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight max-w-3xl">What lands in your inbox</h1>
          <p className="text-lg text-gray-200 leading-relaxed max-w-3xl">
            These are the emails our system sends, shown with a fictitious brand. You hear from us when your application
            moves forward or someone files something similar, and on the last Friday of every month you get the full
            report.
          </p>
        </div>
      </SectionBackground>

      <section className="container mx-auto px-4 py-12 md:py-16">
        <div role="tablist" aria-label="Sample emails" className="flex flex-wrap gap-2 mb-8 md:mb-10">
          {samples.map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={s.key === active}
              onClick={() => choose(s.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
                s.key === active
                  ? 'bg-text text-white border-text'
                  : 'bg-background-white text-text border-gray-200 hover:border-text/40'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <p className="text-primary-dark font-semibold text-sm uppercase tracking-wider mb-3">{sample.when}</p>
            <h2 className="text-3xl font-bold text-text mb-4 leading-tight">{sample.title}</h2>
            <p className="text-text/75 leading-relaxed mb-6">{sample.text}</p>
            <ul className="space-y-3 mb-8">
              {sample.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-text/80 text-sm leading-relaxed">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">
                    ✓
                  </span>
                  <span className="pt-0.5">{p}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              {sample.key === 'ip-firms' ? (
                <a
                  href={PORTFOLIO_MAILTO}
                  className="inline-block bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary-hover font-semibold shadow-md"
                >
                  Request portfolio rates
                </a>
              ) : (
                <a
                  href={CHECKOUT_URL}
                  className="inline-block bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary-hover font-semibold shadow-md"
                >
                  Start watching · US$ 150/year
                </a>
              )}
              <Link
                to="/brazil-watch"
                className="inline-block bg-background-auxiliary text-text px-6 py-3 rounded-xl hover:bg-gray-200 font-semibold"
              >
                How it works
              </Link>
            </div>
          </div>
          <div className="lg:col-span-8 min-w-0">
            <EmailFrame sample={sample} />
          </div>
        </div>

        <p className="mt-10 text-xs text-text/55 max-w-3xl leading-relaxed">
          Illustrative example. NORDA, NORDAH, Norda Cosmetics, Nordah Beauty and Hartwell IP are fictitious, and the
          application numbers are placeholders. The weekly totals are the real counts of applications published in INPI's
          October 2026 gazettes. Alerts are information, not a guarantee of any outcome.
        </p>
      </section>
    </main>
  );
};

export default BrazilWatchSamplePage;
